import Player from './player.js';
import Obstacles from './obstacles.js';
import Score from './score.js';
import Scenery from './scenery.js';

const miniGameWrapper = document.querySelector('.mini-game-wrapper');
const canvas = document.getElementById('mini-game');
const ctx = canvas.getContext('2d');
ctx.imageSmoothingEnabled = false;
const jumpSound = new Audio(new URL('./jump.mp3', import.meta.url).href);
jumpSound.preload = 'auto';
const gameOverSound = new Audio(new URL('./game-over.mp3', import.meta.url).href);
gameOverSound.preload = 'auto';
const gameStartSound = new Audio(new URL('./game-start.mp3', import.meta.url).href);
gameStartSound.preload = 'auto';
const powerUpSound = new Audio(new URL('./powerup.mp3', import.meta.url).href);
powerUpSound.preload = 'auto';

const gameSpeedStart = 0.75;
const colorBlack = '#000000';
const colorWhite = '#ffffff';
const colorSky = '#57b8f4';
const fontFamily = '"Courier New", monospace';

const gameWidth = 1300;
const gameHeight = 220;
const playerWidth = 88 / 1.5;
const playerHeight = 94 / 1.5;
const obstacleWidth = 70;
const obstacleHeight = 70;
const obstacleSpeed = 5.5;
const maxJumpHeight = gameHeight;
const minJumpHeight = 150;

// Game Objects
let player = null;
let obstacles = null;
let score = null;
let scenery = null;

let scaleRatio = null;
let previousTime = null;
let gameSpeed = gameSpeedStart;
let isGameOver = false;
let isIntroAnimating = false;
let hasGameStarted = false;
let isGameVisible = false;
let obstacleSpeedBoostLevel = 0;
let lastRightArrowTapTime = 0;
let lastSpaceTapTime = 0;
let runBoostUntil = 0;
let restartButtonBounds = null;
let closeButtonBounds = null;
let movementScoreTimer = 0;
let extendCurrentJump = false;
const controls = {
  jumpPressed: false,
  rightPressed: false,
};
const runBoostThreshold = 325;
const runBoostDuration = 425;
const longJumpTapThreshold = 325;

function playSound(sound) {
  const soundClone = sound.cloneNode();
  soundClone.play().catch(() => {});
}

function createSprites() {
  const playerWidthGame = playerWidth * scaleRatio;
  const playerHeightGame = playerHeight * scaleRatio;
  const obstacleWidthGame = obstacleWidth * scaleRatio;
  const obstacleHeightGame = obstacleHeight * scaleRatio;
  const minJumpHeightGame = minJumpHeight * scaleRatio;
  const maxJumpHeightGame = maxJumpHeight * scaleRatio;

  if (!player) {
    player = new Player(
      ctx,
      playerWidthGame,
      playerHeightGame,
      maxJumpHeightGame,
      minJumpHeightGame,
      scaleRatio
    );
    player.onJump = () => {
      if (!isGameOver) {
        playSound(jumpSound);
      }
    };
  } else {
    player.resize(
      playerWidthGame,
      playerHeightGame,
      maxJumpHeightGame,
      minJumpHeightGame,
      scaleRatio
    );
  }

  if (!obstacles) {
    obstacles = new Obstacles(
      ctx,
      obstacleWidthGame,
      obstacleHeightGame,
      obstacleSpeed,
      scaleRatio
    );
  } else {
    obstacles.resize(
      obstacleWidthGame,
      obstacleHeightGame,
      obstacleSpeed,
      scaleRatio
    );
  }
  obstacles.setSpeedBoostLevel(obstacleSpeedBoostLevel);

  if (!score) {
    score = new Score(ctx, scaleRatio);
  } else {
    score.resize(scaleRatio);
  }

  if (!scenery) {
    scenery = new Scenery(ctx, scaleRatio);
  } else {
    scenery.resize(scaleRatio);
  }
}

function getScaleRatio() {
  const containerWidth =
    canvas.parentElement?.getBoundingClientRect().width ||
    miniGameWrapper?.getBoundingClientRect().width ||
    window.innerWidth;

  return Math.min(containerWidth / gameWidth, 1);
}

function setScreen() {
  scaleRatio = getScaleRatio();

  canvas.width = gameWidth * scaleRatio;
  canvas.height = gameHeight * scaleRatio;

  canvas.style.width = '100%';
  canvas.style.height = 'auto';

  createSprites();
}

function startGame() {
  if (hasGameStarted) {
    return;
  }

  hasGameStarted = true;
  setScreen();
  requestAnimationFrame(gameLoop);
}

function showGame() {
  isGameVisible = true;
  miniGameWrapper?.classList.remove('is-hidden');
  miniGameWrapper?.setAttribute('aria-hidden', 'false');
}

function closeGame() {
  if (player && obstacles && scenery && score) {
    restartGame();
  }

  isGameVisible = false;
  miniGameWrapper?.classList.add('is-hidden');
  miniGameWrapper?.setAttribute('aria-hidden', 'true');
  restartButtonBounds = null;
  closeButtonBounds = null;
}

// resize set screen size
window.addEventListener('resize', () => {
  if (!hasGameStarted) {
    return;
  }

  setScreen();
});

function isTypingTarget(target) {
  return (
    target instanceof Element &&
    target.closest('input, textarea, select, [contenteditable]') !== null
  );
}

window.addEventListener('keydown', (event) => {
  // Never hijack keystrokes while the user is typing in a form field
  if (isTypingTarget(event.target)) {
    return;
  }

  if (event.shiftKey && event.key.toLowerCase() === 't') {
    showGame();

    if (player && obstacles && scenery && score) {
      restartGame();
    }

    startGame();

    if (miniGameWrapper) {
      const wrapperTop = window.scrollY + miniGameWrapper.getBoundingClientRect().top;

      window.scrollTo({
        top: Math.max(0, wrapperTop - 120),
        behavior: 'smooth',
      });
    }

    player.startIntroDrop();
    isIntroAnimating = true;
    controls.rightPressed = false;
    controls.jumpPressed = false;
    playSound(gameStartSound);
  }

  if (isGameVisible && event.key === 'Escape') {
    event.preventDefault();
    closeGame();
    return;
  }

  if (isGameVisible && isGameOver && (event.key === 'Enter' || event.code === 'Enter')) {
    event.preventDefault();
    restartGame();
    return;
  }

  if (isGameVisible && event.key === 'ArrowRight') {
    const now = event.timeStamp;

    if (!event.repeat && now - lastRightArrowTapTime <= runBoostThreshold) {
      runBoostUntil = now + runBoostDuration;
    }

    if (!event.repeat) {
      lastRightArrowTapTime = now;
    }

    controls.rightPressed = true;
  }

  if (isGameVisible && (event.key === ' ' || event.code === 'Space')) {
    event.preventDefault();
    if (!event.repeat) {
      const now = event.timeStamp;

      if (now - lastSpaceTapTime <= longJumpTapThreshold) {
        extendCurrentJump = true;
      }

      lastSpaceTapTime = now;
    }

    controls.jumpPressed = true;
  }
});

window.addEventListener('keyup', (event) => {
  if (event.key === 'ArrowRight') {
    controls.rightPressed = false;
  }

  if (event.key === ' ' || event.code === 'Space') {
    if (isGameVisible && !isTypingTarget(event.target)) {
      event.preventDefault();
    }
    controls.jumpPressed = false;
  }
});

canvas.addEventListener('pointerdown', (event) => {
  if (!isGameVisible) {
    return;
  }

  const rect = canvas.getBoundingClientRect();
  const pointerX = (event.clientX - rect.left) * (canvas.width / rect.width);
  const pointerY = (event.clientY - rect.top) * (canvas.height / rect.height);

  if (
    closeButtonBounds &&
    pointerX >= closeButtonBounds.x &&
    pointerX <= closeButtonBounds.x + closeButtonBounds.width &&
    pointerY >= closeButtonBounds.y &&
    pointerY <= closeButtonBounds.y + closeButtonBounds.height
  ) {
    closeGame();
    return;
  }

  if (!isGameOver || !restartButtonBounds) {
    return;
  }

  if (
    pointerX >= restartButtonBounds.x &&
    pointerX <= restartButtonBounds.x + restartButtonBounds.width &&
    pointerY >= restartButtonBounds.y &&
    pointerY <= restartButtonBounds.y + restartButtonBounds.height
  ) {
    restartGame();
  }
});

function clearScreen() {
  ctx.fillStyle = colorSky;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}

function drawCloseButton() {
  const padding = Math.round(Math.max(8, 12 * scaleRatio));
  const buttonWidth = Math.round(Math.max(52, 68 * scaleRatio));
  const buttonHeight = Math.round(Math.max(24, 30 * scaleRatio));
  const buttonX = padding;
  const buttonY = padding;
  const innerX = buttonX + 3;
  const innerY = buttonY + 3;
  const innerWidth = buttonWidth - 6;
  const innerHeight = buttonHeight - 6;
  const fontSize = Math.max(12, Math.round(16 * scaleRatio));

  closeButtonBounds = {
    x: buttonX,
    y: buttonY,
    width: buttonWidth,
    height: buttonHeight,
  };

  ctx.fillStyle = colorBlack;
  ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);

  ctx.strokeStyle = colorBlack;
  ctx.lineWidth = Math.max(2, Math.round(3 * scaleRatio));
  ctx.strokeRect(buttonX, buttonY, buttonWidth, buttonHeight);
  ctx.lineWidth = Math.max(1, Math.round(2 * scaleRatio));
  ctx.strokeRect(innerX, innerY, innerWidth, innerHeight);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = colorWhite;
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.fillText('ESC', Math.round(buttonX + buttonWidth / 2), Math.round(buttonY + buttonHeight / 2));
}

function drawGameOver() {
  const centerX = Math.round(canvas.width / 2);
  const centerY = Math.round(canvas.height / 2);
  const panelWidth = Math.round(Math.max(190, 270 * scaleRatio));
  const panelHeight = Math.round(Math.max(58, 76 * scaleRatio));
  const panelX = Math.round(centerX - panelWidth / 2);
  const panelY = Math.round(centerY - panelHeight / 2);
  const titleY = Math.round(panelY + Math.max(16, 20 * scaleRatio));
  const buttonWidth = Math.round(Math.max(96, 144 * scaleRatio));
  const buttonHeight = Math.round(Math.max(24, 30 * scaleRatio));
  const buttonX = Math.round(centerX - buttonWidth / 2);
  const buttonY = Math.round(panelY + panelHeight - buttonHeight - Math.max(3, 5 * scaleRatio));
  const titleFontSize = Math.max(14, Math.round(19 * scaleRatio));
  const buttonFontSize = Math.max(14, Math.round(12.5 * scaleRatio));

  ctx.fillStyle = colorBlack;
  ctx.fillRect(panelX, panelY, panelWidth, panelHeight);

  ctx.strokeStyle = colorBlack;
  ctx.lineWidth = Math.max(2, Math.round(3 * scaleRatio));
  ctx.strokeRect(panelX, panelY, panelWidth, panelHeight);
  ctx.lineWidth = Math.max(1, Math.round(2 * scaleRatio));
  ctx.strokeRect(panelX + 3, panelY + 3, panelWidth - 6, panelHeight - 6);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${titleFontSize}px ${fontFamily}`;
  ctx.fillStyle = colorWhite;
  ctx.fillText('GAME OVER', centerX, titleY);
  restartButtonBounds = {
    x: buttonX,
    y: buttonY,
    width: buttonWidth,
    height: buttonHeight,
  };

  ctx.fillStyle = colorBlack;
  ctx.strokeStyle = colorWhite;
  ctx.lineWidth = 1;
  ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
  ctx.strokeRect(buttonX, buttonY, buttonWidth, buttonHeight);

  ctx.fillStyle = colorWhite;
  ctx.font = `${buttonFontSize}px ${fontFamily}`;
  ctx.fillText('RESTART', centerX, Math.round(buttonY + buttonHeight / 2));
}

function restartGame() {
  isGameOver = false;
  isIntroAnimating = false;
  obstacleSpeedBoostLevel = 0;
  restartButtonBounds = null;
  runBoostUntil = 0;
  movementScoreTimer = 0;
  previousTime = null;
  controls.rightPressed = false;
  controls.jumpPressed = false;
  extendCurrentJump = false;
  score.reset();
  player.reset();
  obstacles.setSpeedBoostLevel(0);
  obstacles.reset();
  scenery.reset();
}

function gameLoop(currentTime) {
  if (!isGameVisible) {
    previousTime = currentTime;
    requestAnimationFrame(gameLoop);
    return;
  }

  if (previousTime === null) {
    previousTime = currentTime;
    requestAnimationFrame(gameLoop);
    return
  }
  // const frameTimeDelta = currentTime - previousTime;
  const frameTimeDelta = Math.min(currentTime - previousTime, 50);
  previousTime = currentTime;

  clearScreen();
  restartButtonBounds = null;
  closeButtonBounds = null;

  const isRunBoosting = controls.rightPressed && currentTime <= runBoostUntil;
  const isPlayerMoving = controls.rightPressed;

  if (isIntroAnimating) {
    isIntroAnimating = player.updateIntroDrop(frameTimeDelta);
  } else if (!isGameOver) {
    const currentScore = score.getCurrentScore();

    if (obstacleSpeedBoostLevel < 2 && currentScore >= 1000) {
      obstacleSpeedBoostLevel = 2;
      obstacles.setSpeedBoostLevel(2);
      playSound(powerUpSound);
    } else if (obstacleSpeedBoostLevel < 1 && currentScore >= 300) {
      obstacleSpeedBoostLevel = 1;
      obstacles.setSpeedBoostLevel(1);
      playSound(powerUpSound);
    }

    player.update(gameSpeed, frameTimeDelta, {
      ...controls,
      extendJump: extendCurrentJump,
      runBoost: isRunBoosting,
    });
    if (player.isJumping) {
      extendCurrentJump = false;
    }
    scenery.update(gameSpeed, frameTimeDelta, isPlayerMoving);
    obstacles.update(gameSpeed, frameTimeDelta, isPlayerMoving);
    score.add(obstacles.collectPassed(player.getBounds()) * 10);

    if (isPlayerMoving) {
      movementScoreTimer += frameTimeDelta;

      while (movementScoreTimer >= 2000) {
        score.add(5);
        movementScoreTimer -= 2000;
      }
    }

    if (obstacles.collidesWith(player.getBounds())) {
      isGameOver = true;
      controls.rightPressed = false;
      controls.jumpPressed = false;
      player.standStill();
      player.startSpin();
      playSound(gameOverSound);
    }
  } else {
    player.updateSpin(frameTimeDelta);
  }

  scenery.draw();
  obstacles.draw();
  player.draw();
  drawCloseButton();
  score.draw();

  if (isGameOver) {
    drawGameOver();
  }

  requestAnimationFrame(gameLoop);
}
